import React from 'react';
import { styled } from '@linaria/react';
import { X as XIcon, ArrowLeft } from 'lucide-react';

import { BREAKPOINTS } from '@/constants';

import ModalBase from '@/components/ModalBase';
import Paper from '@/components/Paper';
import ParagraphRaw from '@/components/Paragraph';
import Spacer from '@/components/Spacer';
import List, { ListItem } from '@/components/List';
import TextLink from '@/components/Link/TextLink';

function SuccessModal({
  email,
  isOpen,
  handleDismiss,
}: {
  email: string | null;
  isOpen: boolean;
  handleDismiss: () => void;
}) {
  return (
    <ModalBase
      isOpen={isOpen}
      handleDismiss={handleDismiss}
      dismissDuration={400}
      overlayProps={{
        exitDelay: 0,
      }}
    >
      <Modal
        aria-label="Confirmation message"
        style={{ opacity: isOpen ? 1 : 0 }}
      >
        {isOpen && (
          <CloseButton
            onClick={handleDismiss}
            aria-label="Dismiss confirmation message"
            data-cy="dismiss-modal"
          >
            <XIcon size={32} />
          </CloseButton>
        )}
        <Content>
          <h2>You've got it!</h2>
          <Spacer size={24} />
          <Paragraph>
            I've sent an email to <strong>{email}</strong> with links
            to download my book. It should arrive imminently!
          </Paragraph>
          <Paragraph>
            You might also be interested in checking out some of my
            blog posts? Here are some of the most popular:
          </Paragraph>
          <StyledList>
            <ListItem>
              <TextLink href="https://www.joshwcomeau.com/blog/how-to-learn-stuff-quickly/">
                How to Learn Stuff Quickly
              </TextLink>
            </ListItem>
            <ListItem>
              <TextLink href="https://www.joshwcomeau.com/animation/css-transitions/">
                An Interactive Guide to CSS Transitions
              </TextLink>
            </ListItem>
            <ListItem>
              <TextLink href="https://www.joshwcomeau.com/css/css-variables-for-react-devs/">
                CSS Variables for React Developers
              </TextLink>
            </ListItem>
          </StyledList>
        </Content>
      </Modal>
    </ModalBase>
  );
}

const Modal = styled(Paper)`
  background: var(--color-background);
  border-radius: 6px;
  width: 570px;
  max-width: 100vw;
  padding: 32px;
  outline: none;
  box-shadow:
    0 2.8px 2.2px rgba(0, 0, 0, 0.02),
    -1px 6.7px 5.3px rgba(0, 0, 0, 0.028),
    -2px 12.5px 10px rgba(0, 0, 0, 0.035),
    -5px 22.3px 17.9px rgba(0, 0, 0, 0.042),
    -10px 41.8px 33.4px rgba(0, 0, 0, 0.05),
    -20px 100px 80px rgba(0, 0, 0, 0.07);
  transition: opacity 300ms;
  animation: fadeIn 400ms;

  @media ${BREAKPOINTS.smAndSmaller} {
    width: 100%;
    max-height: 100vh;
    max-height: 100svh;
    overflow: auto;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  padding: 16px;
  transform: translateY(-100%);
  color: var(--color-text);

  html[data-color-mode='dark'] & {
    color: white;
  }

  @media ${BREAKPOINTS.smAndSmaller} {
    html[data-color-mode] & {
      z-index: 2;
      transform: revert;
      color: black;
    }
  }
`;

const Content = styled.div`
  animation: fadeIn 300ms 300ms both;
`;

const StyledList = styled(List)`
  margin-bottom: 0;
  font-size: 1.125rem;
`;

const Paragraph = styled(ParagraphRaw)`
  margin-bottom: 24px;
  font-size: 1.125rem;
`;
export default SuccessModal;
